import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@safetyhub/ui';
import { adminApi } from '../lib/api';

// 날짜 범위 옵션
type DateRangeOption = 'this_week' | 'this_month' | 'last_3_months';

const DATE_RANGE_OPTIONS: { value: DateRangeOption; label: string }[] = [
  { value: 'this_week', label: '이번 주' },
  { value: 'this_month', label: '이번 달' },
  { value: 'last_3_months', label: '최근 3개월' },
];

// 프로바이더 정보
const PROVIDER_INFO: Record<string, { name: string; color: string }> = {
  openai: { name: 'OpenAI', color: 'emerald' },
  claude: { name: 'Anthropic Claude', color: 'violet' },
  gemini: { name: 'Google Gemini', color: 'blue' },
};

// 데이터 소스 라벨
const SOURCE_LABELS: Record<string, { label: string; description: string }> = {
  direct: { label: '직접 계산', description: '자체 DB 기반 토큰 사용량 추정' },
  provider_api: { label: 'Provider API', description: '공식 API를 통한 실제 사용량 조회' },
};

// 날짜 범위 계산 함수
function getDateRange(option: DateRangeOption): { startDate: string; endDate: string } {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0];
  let startDate: string;

  switch (option) {
    case 'this_week': {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay()); // 이번 주 일요일
      startDate = start.toISOString().split('T')[0];
      break;
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate = start.toISOString().split('T')[0];
      break;
    }
    case 'last_3_months': {
      const start = new Date(now);
      start.setMonth(now.getMonth() - 3);
      startDate = start.toISOString().split('T')[0];
      break;
    }
  }

  return { startDate, endDate };
}

// 숫자 포맷 함수
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
}

// 비용 포맷 함수
function formatCost(cost: number): string {
  return `$${cost.toFixed(2)}`;
}

// 날짜 포맷 함수 (사용자 친화적)
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function AIUsageStats() {
  const [dateRange, setDateRange] = useState<DateRangeOption>('this_week');
  const { startDate, endDate } = getDateRange(dateRange);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'ai-usage', startDate, endDate],
    queryFn: () => adminApi.getAIUsage({ startDate, endDate }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const usage = data.data!;
  const successRate =
    usage.summary.totalRequests > 0
      ? ((usage.summary.successfulRequests / usage.summary.totalRequests) * 100).toFixed(1)
      : '0';

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">AI 사용량 통계</h2>
        <p className="mt-1 text-sm text-gray-500">
          AI 프로바이더별 사용량과 비용을 확인합니다.
        </p>
      </div>

      {/* 필터 영역 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">기간:</label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            {formatDate(usage.period.start)} ~ {formatDate(usage.period.end)}
          </span>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          새로고침
        </button>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <SummaryCard
          title="총 요청"
          value={formatNumber(usage.summary.totalRequests)}
          icon={<RequestIcon />}
          color="blue"
        />
        <SummaryCard
          title="성공률"
          value={`${successRate}%`}
          icon={<SuccessIcon />}
          color="green"
          subValue={`${usage.summary.successfulRequests.toLocaleString()} / ${usage.summary.totalRequests.toLocaleString()}`}
        />
        <SummaryCard
          title="총 토큰"
          value={formatNumber(usage.summary.totalTokens)}
          icon={<TokenIcon />}
          color="purple"
        />
        <SummaryCard
          title="총 비용"
          value={formatCost(usage.summary.totalCost)}
          icon={<CostIcon />}
          color="orange"
        />
      </div>

      {/* 요청 타입별 평균 응답 시간 */}
      {usage.summary.latencyByRequestType && usage.summary.latencyByRequestType.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {usage.summary.latencyByRequestType
            .filter((item) => item.requestType !== 'UNKNOWN')
            .map((item) => (
              <SummaryCard
                key={item.requestType}
                title={`${item.label} 평균`}
                value={`${item.avgLatencySec}초`}
                icon={<ClockIcon />}
                color="blue"
                subValue={`${item.count.toLocaleString()}건 (${(item.minRangeMs / 1000).toFixed(1)}~${(item.maxRangeMs / 1000).toFixed(1)}초)`}
              />
            ))}
        </div>
      )}

      {/* 프로바이더별 상세 */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">프로바이더별 상세</h3>
      </div>

      <div className="space-y-4">
        {Object.entries(usage.byProvider).length === 0 ? (
          <div className="bg-gray-50 p-8 rounded-card text-center text-gray-500">
            해당 기간에 사용된 AI 프로바이더가 없습니다.
          </div>
        ) : (
          Object.entries(usage.byProvider).map(([provider, providerData]) => (
            <ProviderCard key={provider} provider={provider} data={providerData} />
          ))
        )}
      </div>

      {/* 데이터 소스 안내 */}
      <div className="mt-8 bg-gray-50 rounded-card p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">데이터 소스 안내</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {Object.entries(SOURCE_LABELS).map(([key, value]) => (
            <div key={key} className="flex items-start gap-2">
              <SourceBadge source={key as 'direct' | 'provider_api'} />
              <span className="text-gray-600">{value.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 요약 카드 컴포넌트
function SummaryCard({
  title,
  value,
  icon,
  color,
  subValue,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'orange';
  subValue?: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <Card padding="xl" radius="lg">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
        </div>
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClasses[color]}`}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

// 프로바이더 카드 컴포넌트
function ProviderCard({
  provider,
  data,
}: {
  provider: string;
  data: {
    source: 'direct' | 'provider_api';
    requests: number;
    successfulRequests: number;
    failedRequests: number;
    tokens: { input: number; output: number; total: number };
    cost: number;
    averageLatency: number;
    models: Record<string, { requests: number; tokens: number; cost: number }>;
    latencyByRequestType: Array<{
      requestType: string;
      label: string;
      count: number;
      avgLatencyMs: number;
      avgLatencySec: number;
      stddevLatencyMs: number;
      minRangeMs: number;
      maxRangeMs: number;
    }>;
  };
}) {
  const info = PROVIDER_INFO[provider] || { name: provider, color: 'gray' };

  return (
    <Card padding="none" radius="lg" className="overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-3 h-3 rounded-full ${
              info.color === 'emerald'
                ? 'bg-emerald-500'
                : info.color === 'violet'
                  ? 'bg-violet-500'
                  : info.color === 'blue'
                    ? 'bg-blue-500'
                    : 'bg-gray-500'
            }`}
          />
          <h4 className="font-semibold text-gray-900">{info.name}</h4>
        </div>
        <SourceBadge source={data.source} />
      </div>

      <div className="px-6 py-4">
        {/* 통계 행 */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500">요청</p>
            <p className="text-lg font-semibold text-gray-900">
              {data.requests.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">성공</p>
            <p className="text-lg font-semibold text-green-600">
              {data.successfulRequests.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">토큰</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatNumber(data.tokens.total)}
            </p>
            <p className="text-xs text-gray-400">
              In: {formatNumber(data.tokens.input)} / Out: {formatNumber(data.tokens.output)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">비용</p>
            <p className="text-lg font-semibold text-orange-600">{formatCost(data.cost)}</p>
          </div>
        </div>

        {/* 모델별 상세 */}
        {Object.keys(data.models).length > 0 && (
          <div className="border-t pt-4">
            <p className="text-xs text-gray-500 mb-2">모델별 상세</p>
            <div className="space-y-2">
              {/* 성공한 요청 (unknown 제외) 먼저 표시 */}
              {Object.entries(data.models)
                .filter(([model]) => model !== 'unknown')
                .map(([model, modelData]) => (
                  <div
                    key={model}
                    className="flex items-center justify-between text-sm bg-gray-50 px-3 py-2 rounded"
                  >
                    <span className="font-mono text-gray-700">{model}</span>
                    <div className="flex items-center gap-4 text-gray-600">
                      <span>{modelData.requests.toLocaleString()}건</span>
                      <span>{formatNumber(modelData.tokens)} 토큰</span>
                      <span className="text-orange-600">{formatCost(modelData.cost)}</span>
                    </div>
                  </div>
                ))}
              {/* 실패한 요청 (unknown) */}
              {data.models['unknown'] && (
                <div className="flex items-center justify-between text-sm bg-red-50 px-3 py-2 rounded border border-red-100">
                  <span className="text-red-600 flex items-center gap-2">
                    <FailedIcon />
                    실패한 요청
                  </span>
                  <div className="flex items-center gap-4 text-red-600">
                    <span>{data.models['unknown'].requests.toLocaleString()}건</span>
                    <span className="text-gray-400">비용 미발생</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 요청 타입별 응답 시간 */}
        {data.latencyByRequestType && data.latencyByRequestType.length > 0 && (
          <div className="border-t pt-4 mt-4">
            <p className="text-xs text-gray-500 mb-2">요청 타입별 응답 시간</p>
            <div className="space-y-1">
              {data.latencyByRequestType.map((item) => (
                <div
                  key={item.requestType}
                  className="flex items-center justify-between text-sm bg-brand-50 px-3 py-2 rounded"
                >
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-800">
                    {item.label}
                  </span>
                  <div className="flex items-center gap-4 text-gray-600 text-xs">
                    <span>{item.count.toLocaleString()}건</span>
                    <span className="font-medium text-brand-700">평균 {item.avgLatencySec}초</span>
                    <span className="text-gray-400" title="일반 범위 (μ±σ, 약 68%)">
                      ({(item.minRangeMs / 1000).toFixed(1)}~{(item.maxRangeMs / 1000).toFixed(1)}초)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// 데이터 소스 뱃지 컴포넌트
function SourceBadge({ source }: { source: 'direct' | 'provider_api' }) {
  const { label, description } = SOURCE_LABELS[source];
  const isProviderApi = source === 'provider_api';

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
        isProviderApi
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-600'
      }`}
      title={description}
    >
      {label}
    </span>
  );
}

// 아이콘 컴포넌트들
function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function RequestIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function FailedIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function TokenIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
      />
    </svg>
  );
}

function CostIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
